// The Home queue's reads (spec 044 D2). Everything runs in one Promise.all;
// names are looked up only for the rows that will render (≤ 5 per group).
//
// Every query here is served by an index the project already has — this file
// must not need a new one (plan-slice2 Global Constraints):
//   tickets  (status ==, orderBy raisedAt desc)      → composite (status, raisedAt DESC)
//   tickets  count(status ==)                        → single-field
//   bookings (status ==, startTimeUTC <=, orderBy startTimeUTC desc)
//                                                    → composite (status, startTimeUTC DESC)
//   bookings count(status ==, startTimeUTC <=)       → either (status, startTimeUTC)
//                                                      composite (no orderBy)
//   users    (role ==, verifyStatus ==)              → two equalities, merge-joined
//   users    count(role ==, verifyStatus ==)         → two equalities, merge-joined
//   users    (unpaidEarning > 0 [, orderBy unpaidEarning desc]) → single-field
//   users    (isInternal == true)                                → single-field
import {
  collection,
  getDocs,
  getCountFromServer,
  getAggregateFromServer,
  query,
  where,
  orderBy,
  limit,
  count,
  sum,
} from "firebase/firestore";
import { db } from "./firebase";
import { getUserById, getPayCycleVersions } from "./dataService";
import { cyclePeriod } from "../utils/payCycle";
import {
  ROWS_PER_GROUP,
  BOOKINGS_WINDOW,
  TICKETS_WINDOW,
  VERIFY_WINDOW,
  pendingCutoff,
  displayName,
  ticketRow,
  lessonRowFromTicket,
  lessonRowFromBooking,
  mergeLessonRows,
  lessonCount,
  verificationRow,
  sortVerificationRows,
  payoutRow,
  periodText,
  peopleIds,
  withNames,
} from "../utils/queuePresentation";
import { withoutInternal } from "../utils/cyclePay";

const docsOf = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

const ticketsWithStatus = (status) =>
  getDocs(
    query(
      collection(db, "tickets"),
      where("status", "==", status),
      orderBy("raisedAt", "desc"),
      limit(TICKETS_WINDOW)
    )
  );

// One getUserById per unique id across the rows that render; a missing or
// unreadable doc resolves to its id, the same fallback the Inbox shows.
const resolveNames = async (rows) => {
  const ids = peopleIds(rows);
  const pairs = await Promise.all(ids.map(async (id) => [id, await getUserById(id)]));
  const people = Object.fromEntries(pairs);
  const nameOf = (id) => displayName(people[id], id);
  return rows.map((row) => withNames(row, nameOf));
};

export const getQueue = async ({ includePayouts, now = new Date() }) => {
  const tickets = collection(db, "tickets");
  const bookings = collection(db, "bookings");
  const users = collection(db, "users");
  const pendingEnded = query(
    bookings,
    where("status", "==", "Pending"),
    where("startTimeUTC", "<=", pendingCutoff(now))
  );

  const [openSnap, waitingSnap, openCount, pendingSnap, pendingCount, verifySnap, verifyCount, payoutBits] =
    await Promise.all([
      ticketsWithStatus("open"),
      ticketsWithStatus("waiting_user"),
      getCountFromServer(query(tickets, where("status", "==", "open"))),
      getDocs(query(pendingEnded, orderBy("startTimeUTC", "desc"), limit(BOOKINGS_WINDOW))),
      getCountFromServer(pendingEnded),
      getDocs(
        query(users, where("role", "==", "teacher"), where("verifyStatus", "==", "pending"), limit(VERIFY_WINDOW))
      ),
      getCountFromServer(
        query(users, where("role", "==", "teacher"), where("verifyStatus", "==", "pending"))
      ),
      includePayouts
        ? Promise.all([
            getAggregateFromServer(query(users, where("unpaidEarning", ">", 0)), {
              owedCount: count(),
              owedTotal: sum("unpaidEarning"),
            }),
            getDocs(
              query(users, where("unpaidEarning", ">", 0), orderBy("unpaidEarning", "desc"), limit(ROWS_PER_GROUP * 2))
            ),
            getPayCycleVersions(),
            // 053 D11: internal (test) accounts leave the money totals — read apart, subtracted.
            getDocs(query(users, where("isInternal", "==", true))),
          ])
        : null,
    ]);

  const open = docsOf(openSnap);
  const stillLive = [...open, ...docsOf(waitingSnap)];

  // Tickets waiting on us: status "open" only — waiting_user is the person's turn.
  const ticketRows = await resolveNames(open.slice(0, ROWS_PER_GROUP).map((t) => ticketRow(t, now)));

  // Lessons needing a decision: reports whose ticket is still live, plus Pending lessons that ended.
  const reported = stillLive.map((t) => lessonRowFromTicket(t, now)).filter(Boolean);
  const pending = docsOf(pendingSnap).map((b) => lessonRowFromBooking(b, now));
  const lessonRows = await resolveNames(mergeLessonRows(reported, pending).slice(0, ROWS_PER_GROUP));

  const verifyRows = sortVerificationRows(docsOf(verifySnap).map(verificationRow));

  let payouts = null;
  if (payoutBits) {
    const [aggregate, owedSnap, versions, internalSnap] = payoutBits;
    const period = periodText(cyclePeriod(versions, now, -1));
    const real = withoutInternal(
      { count: aggregate.data().owedCount, total: aggregate.data().owedTotal },
      docsOf(internalSnap)
    );
    payouts = {
      count: real.count,
      total: real.total,
      // ponytail: twice the rows shown, enough while fewer than ROWS_PER_GROUP internal tutors owe.
      rows: docsOf(owedSnap).filter((t) => !t.isInternal).slice(0, ROWS_PER_GROUP).map((t) => payoutRow(t, period)),
      period,
    };
  }

  return {
    tickets: { count: openCount.data().count, rows: ticketRows },
    verification: { count: verifyCount.data().count, rows: verifyRows.slice(0, ROWS_PER_GROUP) },
    lessons: {
      count: lessonCount({ reported, pending, pendingCount: pendingCount.data().count }),
      rows: lessonRows,
    },
    payouts,
  };
};
